import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { getEventInfo, getFamilyColor } from "./eventDict";
import { humanizeUploadFailure } from "./uploadFailureLabels";
import {
  extractLayoutUuidFromGroup,
  isLayoutUuid,
  isRealCustomerRef,
} from "./layoutLinkUtils";
import { formatSessionIdentityLabel } from "./accountCreatedEnrichment";
import { ANALYTICS_FONT_FAMILY } from "./analyticsTypography";
import { MF_SUPPORT_URL } from "../../utils/legalLinks";
import {
  ANALYTICS_GREEN,
  ANALYTICS_RED,
  analyticsGreenAlpha,
  analyticsRedAlpha,
} from "./analyticsColors";

// Sessions whose most-recent event is within this window show a live pulse.
const LIVE_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes

// Number of session cards to reveal per "Load more" click.
const SESSIONS_PER_PAGE = 3;

const formatLinkedListingLabel = (linkedRef) => {
  if (!linkedRef) return 'Linked';
  const ref = String(linkedRef);
  if (ref.length <= 4) return `Linked (${ref})`;
  return `Linked (...${ref.slice(-4)})`;
};

/**
 * Extract the canonical layout ID from a single event row.
 *
 * Most events carry the layout's MongoDB _id in `detail.galleryId`.
 * `gallery_ready` uses `detail.uniqueId`.
 * `floorplan_uploaded` carries only {fileName, fileSize} — no id — and is
 * handled separately via a time-window proximity pass below.
 */
function extractLayoutId(row) {
  return row.detail?.galleryId || row.detail?.uniqueId || null;
}

/**
 * MongoDB ObjectId format check: 24 lowercase or uppercase hex characters.
 *
 * Used by the gallery URL fallback below — the `/floor-gallery/:galleryId`
 * route resolves layouts via `/savedLayout/getLayoutVersionsByID/:id`, which
 * is ObjectId-only in practice. Editor events log `galleryId: layoutData.uuid
 * || layoutData._id`, so groups whose oldest events are *editor* events end
 * up with a UUID-format layoutId. Constructing `/floor-gallery/{UUID}` from
 * those would render a link that opens an infinite spinner. We use this check
 * to decide whether the constructed fallback URL is safe to render.
 */
function isLikelyObjectId(s) {
  return typeof s === "string" && /^[0-9a-fA-F]{24}$/.test(s);
}

/**
 * Check whether a URL is a well-formed gallery URL whose trailing path
 * segment is a MongoDB ObjectId (24 hex chars, no dashes). Used to
 * distinguish authoritative gallery URLs from editor / UUID-based URLs
 * that may appear in `detail.galleryUrl` on non-gallery_ready events.
 */
export function isValidGalleryUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trailing = url.split("?")[0].split("/").filter(Boolean).pop();
  return isLikelyObjectId(trailing);
}

/**
 * Group feed events by layout (one card per floor plan).
 *
 * Pass 1 — group every event that carries a layoutId (galleryId / uniqueId).
 *   All activity on the same floor plan ends up in one bucket regardless of
 *   which user fired the event, so pre-auth and post-auth events (Q5) and
 *   agent vs viewer events (Q4) are naturally unified.
 *
 * Pass 2 — associate orphan events that have no layoutId (currently just
 *   `floorplan_uploaded`, which fires before processing assigns an id).
 *   Match each orphan to the closest group from the same meta.user whose
 *   earliest event is within 15 minutes after the orphan's timestamp.
 *   Unmatched orphans become standalone single-event groups.
 *
 * The customer_ref (S.MPLE listing ref) is stored as `group.ref` for display
 * but is no longer the grouping key — every upload now has its own card.
 *
 * `rows` must arrive sorted newest-first (the `sorted` useMemo in FeedList
 * guarantees this). The insertion order of `order` therefore preserves the
 * newest-first ordering for groups.
 */
export function groupEventsByUpload(rows) {
  const groups = {};
  const order = [];

  const orphans = []; // events with no extractable layoutId

  // ── Pass 1: group by layoutId ──────────────────────────────────────────
  rows.forEach((row) => {
    const layoutId = extractLayoutId(row);

    if (!layoutId) {
      orphans.push(row);
      return;
    }

    if (!groups[layoutId]) {
      groups[layoutId] = { key: layoutId, ref: null, rows: [] };
      order.push(layoutId);
    }

    const g = groups[layoutId];
    // Prefer customer_ref if we encounter it (may appear on any event)
    if (!g.ref) {
      const rawRef = row.meta?.customer_ref;
      if (rawRef && !String(rawRef).includes("not-provided")) g.ref = rawRef;
    }
    g.rows.push(row);
  });

  // ── Pass 2: associate orphan events (floorplan_uploaded etc.) ────────────
  const ASSOC_WINDOW_MS = 15 * 60 * 1000; // 15 minutes

  orphans.forEach((orphan) => {
    // upload_failed must never be associated with a gallery_ready group —
    // doing so would attach an error event from a rejected attempt onto a
    // completely separate successful upload card (same user, <15 min later).
    // Route it straight to a standalone orphan so Pass 3 can merge it with
    // the matching floorplan_uploaded orphan, or leave it standalone for
    // client-side validation failures that have no floorplan_uploaded peer.
    if (orphan.meta?.event === "upload_failed") {
      const orphanTime = new Date(orphan.timestamp || 0).getTime();
      const fallbackKey = `orphan_${orphan.meta?.user || orphan._id}_${orphanTime}`;
      if (!groups[fallbackKey]) {
        groups[fallbackKey] = { key: fallbackKey, ref: null, rows: [] };
        order.push(fallbackKey);
      }
      const rawRef = orphan.meta?.customer_ref;
      if (rawRef && !String(rawRef).includes("not-provided")) {
        groups[fallbackKey].ref = rawRef;
      }
      groups[fallbackKey].rows.push(orphan);
      return;
    }

    // account_created orphans (from the /signup page — no galleryId) are
    // always standalone cards. Never associate them with nearby layout groups
    // since signup precedes any layout activity for that user.
    if (orphan.meta?.event === "account_created") {
      const orphanTime = new Date(orphan.timestamp || 0).getTime();
      const fallbackKey = `orphan_${orphan.meta?.user || orphan._id}_${orphanTime}`;
      if (!groups[fallbackKey]) {
        groups[fallbackKey] = { key: fallbackKey, ref: null, rows: [] };
        order.push(fallbackKey);
      }
      groups[fallbackKey].rows.push(orphan);
      return;
    }

    // Prefer guestSessionId to pair orphan events — same fallback pattern
    // used in EditorEngagementCard. Falls back to meta.user when guestSessionId
    // is absent (legacy events logged before the guestSessionId BE rollout).
    // For two distinct guests with new data, their guestSessionIds differ so
    // they no longer cross-contaminate.
    const orphanSessionKey =
      orphan.meta?.guestSessionId || orphan.meta?.user;
    const orphanTime = new Date(orphan.timestamp || 0).getTime();

    let bestKey = null;
    let bestDelta = Infinity;

    // Find the nearest group from the same session whose earliest event is
    // within the window and comes AFTER the orphan (upload precedes processing)
    Object.values(groups).forEach((g) => {
      if (
        !g.rows.some(
          (r) => (r.meta?.guestSessionId || r.meta?.user) === orphanSessionKey
        )
      ) {
        return;
      }
      const groupStart = new Date(
        g.rows.reduce(
          (min, r) => (!min || new Date(r.timestamp) < new Date(min) ? r.timestamp : min),
          null
        )
      ).getTime();
      const delta = groupStart - orphanTime; // positive = group is later
      if (delta >= 0 && delta < ASSOC_WINDOW_MS && delta < bestDelta) {
        bestDelta = delta;
        bestKey = g.key;
      }
    });

    if (bestKey) {
      groups[bestKey].rows.push(orphan);
    } else {
      // No nearby group — create a standalone orphan card
      const fallbackKey = `orphan_${orphan.meta?.guestSessionId || orphan.meta?.user || orphan._id}_${orphanTime}`;
      if (!groups[fallbackKey]) {
        groups[fallbackKey] = { key: fallbackKey, ref: null, rows: [] };
        order.push(fallbackKey);
      }
      const rawRef = orphan.meta?.customer_ref;
      if (rawRef && !String(rawRef).includes("not-provided")) {
        groups[fallbackKey].ref = rawRef;
      }
      groups[fallbackKey].rows.push(orphan);
    }
  });

  // ── Sort each group's rows ascending (oldest first) for the timeline ─────

  // ── Pass 3: merge standalone upload_failed orphans into the nearest ───────
  // floorplan_uploaded orphan from the same user. For server-side pipeline
  // failures both events fire independently and become separate orphan cards
  // in Passes 1–2; this pass unifies them into one "Upload failed" card.
  // Client-side validation failures (wrong type, too large) never fire
  // floorplan_uploaded, so those upload_failed orphans have no peer and
  // remain as standalone "Upload failed" cards — which is correct.
  const FAIL_ASSOC_WINDOW_MS = 5 * 60 * 1000; // 5 minutes
  const orphanKeysSnapshot = order.filter((k) => k.startsWith("orphan_"));

  orphanKeysSnapshot.forEach((failKey) => {
    const failGroup = groups[failKey];
    if (!failGroup) return;
    const hasFailure = failGroup.rows.some((r) => r.meta?.event === "upload_failed");
    const hasUpload = failGroup.rows.some((r) => r.meta?.event === "floorplan_uploaded");
    if (!hasFailure || hasUpload) return; // only care about pure failure orphans

    const failEvent = failGroup.rows.find((r) => r.meta?.event === "upload_failed");
    const failUser = failEvent?.meta?.user;
    const failTime = new Date(failEvent?.timestamp || 0).getTime();

    let bestKey = null;
    let bestDelta = Infinity;

    // Search ALL groups (orphans and non-orphans) for the matching
    // floorplan_uploaded event. Originally this only scanned orphanKeysSnapshot,
    // but floorplan_uploaded events carry a layoutId so they land in non-orphan
    // groups during Pass 1 — making them invisible to an orphan-only scan.
    order.forEach((uploadKey) => {
      if (uploadKey === failKey) return;
      const uploadGroup = groups[uploadKey];
      if (!uploadGroup) return;
      const uploadEvent = uploadGroup.rows.find(
        (r) => r.meta?.event === "floorplan_uploaded"
      );
      if (!uploadEvent) return;
      if (uploadEvent.meta?.user !== failUser) return;
      const uploadTime = new Date(uploadEvent.timestamp || 0).getTime();
      const delta = failTime - uploadTime; // positive = failure is after upload
      if (delta >= 0 && delta < FAIL_ASSOC_WINDOW_MS && delta < bestDelta) {
        bestDelta = delta;
        bestKey = uploadKey;
      }
    });

    if (bestKey) {
      failGroup.rows.forEach((r) => groups[bestKey].rows.push(r));
      delete groups[failKey];
      const idx = order.indexOf(failKey);
      if (idx !== -1) order.splice(idx, 1);
    }
  });

  // ── Pass 4: merge same-user engagement-only groups into upload/furnish ───────
  // Root cause: UploadFloor.js historically logged galleryId as layoutData._id
  // (MongoDB ObjectId) while gallery_ready used uniqueId (UUID). When these
  // differ, editor events (shuffle, share, etc.) land in a separate group from
  // the upload/furnish events. Pass 4 reunifies them by finding engagement-only
  // non-orphan groups whose events are all from the same user as a gallery_ready
  // group, occur within 2 hours after that gallery_ready, and contain no
  // webview_opened (which would indicate a separate new-session view rather than
  // a continuation of the same upload session). The nearest match wins.
  //
  // Note: UploadFloor.js now emits layoutData.uuid || layoutData._id so this
  // pass is a safety net for historical events and residual edge cases.
  const SAME_USER_MERGE_WINDOW_MS = 2 * 60 * 60 * 1000; // 2 hours

  const nonOrphanSnapshot = order.filter((k) => !k.startsWith("orphan_"));

  nonOrphanSnapshot.forEach((engKey) => {
    const engGroup = groups[engKey];
    if (!engGroup) return;

    // Only consider engagement-only groups (no upload/furnish anchor events).
    const hasAnchor = engGroup.rows.some(
      (r) =>
        r.meta?.event === "gallery_ready" ||
        r.meta?.event === "floorplan_uploaded"
    );
    if (hasAnchor) return;

    // Determine the primary user for this engagement group.
    const engUser = engGroup.rows.find((r) => r.meta?.user)?.meta?.user;
    if (!engUser) return;

    // webview_opened signals a new browsing session — typically a *different*
    // user opening a shared link. Skip the merge only when the webview_opened
    // came from a user other than the uploader. When the *same* user fired
    // the webview_opened (a guest agent visiting their own /floor-gallery
    // result page after upload), it's a continuation of the upload session,
    // not a separate viewer, and SHOULD merge into the upload card. Without
    // this same-user awareness, every guest upload produced a phantom
    // "Gallery View" card alongside its legitimate "Upload & Edit" card.
    const webviewByOtherUser = engGroup.rows.some(
      (r) =>
        r.meta?.event === "webview_opened" && r.meta?.user !== engUser
    );
    if (webviewByOtherUser) return;

    const engEarliestTime = Math.min(
      ...engGroup.rows.map((r) => new Date(r.timestamp || 0).getTime())
    );

    let bestKey = null;
    let bestDelta = Infinity;

    nonOrphanSnapshot.forEach((uploadKey) => {
      if (uploadKey === engKey) return;
      const uploadGroup = groups[uploadKey];
      if (!uploadGroup) return;

      // The upload group must have a gallery_ready from this exact user.
      const galleryReadyEvt = uploadGroup.rows.find(
        (r) =>
          r.meta?.event === "gallery_ready" && r.meta?.user === engUser
      );
      if (!galleryReadyEvt) return;

      const grTime = new Date(galleryReadyEvt.timestamp || 0).getTime();
      const delta = engEarliestTime - grTime; // positive = engagement is after

      if (delta >= 0 && delta < SAME_USER_MERGE_WINDOW_MS && delta < bestDelta) {
        bestDelta = delta;
        bestKey = uploadKey;
      }
    });

    if (bestKey) {
      engGroup.rows.forEach((r) => groups[bestKey].rows.push(r));
      if (!groups[bestKey].ref && engGroup.ref) {
        groups[bestKey].ref = engGroup.ref;
      }
      delete groups[engKey];
      const idx = order.indexOf(engKey);
      if (idx !== -1) order.splice(idx, 1);
    }
  });

  // ── Capture authoritative galleryUrl from any event that carries one ──────
  // Historically only `gallery_ready` carried `detail.galleryUrl`. With Path A
  // logging, every editor event (shuffle_room, editor_opened, layout_saved,
  // etc.) now also embeds a galleryUrl built from `layoutData._id` — an
  // ObjectId-based URL that resolves correctly. We prefer `gallery_ready`'s
  // URL (most canonical) but fall back to any other event that has a valid
  // gallery URL (ObjectId-trailing, no dashes). Split-off chunks inherit the
  // parent galleryUrl at the end of the split pass below.
  order.forEach((key) => {
    const g = groups[key];
    if (!g || key.startsWith("orphan_")) return;
    // Priority 1 — gallery_ready (most authoritative, backend-issued URL)
    const grEvt = g.rows.find((r) => r.meta?.event === "gallery_ready");
    if (grEvt?.detail?.galleryUrl) {
      g.galleryUrl = grEvt.detail.galleryUrl;
      return;
    }
    // Priority 2 — any other event with a valid ObjectId-based gallery URL
    // (e.g. editor_opened, shuffle_room, webview_opened logged with Path A)
    const rowWithUrl = g.rows.find(
      (r) => r.detail?.galleryUrl && isValidGalleryUrl(r.detail.galleryUrl)
    );
    if (rowWithUrl) g.galleryUrl = rowWithUrl.detail.galleryUrl;
  });

  // ── Cross-group galleryUrl propagation ──────────────────────────────────
  // The capture step above sets `galleryUrl` on any group whose events carry
  // a valid gallery URL (gallery_ready preferred, editor events as fallback
  // after Path A logging). But viewer sessions may still be in their own
  // separate group with no URL-bearing events at all (e.g., a viewer who
  // never triggered an editor event, or a session where the upload group is
  // entirely outside the loaded time window).
  //
  // Build a map of every known layout identifier → galleryUrl by walking
  // each URL-bearing group's events (collecting `galleryId`, `uniqueId`, and
  // the URL's trailing path segment). Then, for each group missing a URL,
  // try to find a match using its own event IDs and group key. This recovers
  // working URLs for cross-group cases where Pass 4 didn't (or shouldn't)
  // merge the groups together — e.g., a pure gallery viewer whose session
  // shares a layout ID with an editor group that does have a URL.
  //
  // For groups that remain without a URL after both passes, the
  // constructedGalleryUrl fallback in RefGroupCard handles ObjectId-keyed
  // groups; UUID-keyed groups render no link (pre-Path-A historical data).
  const urlByLayoutId = new Map();
  order.forEach((key) => {
    const g = groups[key];
    if (!g?.galleryUrl) return;
    const register = (id) => {
      if (id && typeof id === "string" && !urlByLayoutId.has(id)) {
        urlByLayoutId.set(id, g.galleryUrl);
      }
    };
    g.rows.forEach((r) => {
      register(r.detail?.galleryId);
      register(r.detail?.uniqueId);
    });
    // Trailing path segment of the URL is the canonical ID the route resolves.
    try {
      const trailing = String(g.galleryUrl).split("?")[0].split("/").filter(Boolean).pop();
      register(trailing);
    } catch {
      /* ignore malformed URLs */
    }
  });

  if (urlByLayoutId.size > 0) {
    order.forEach((key) => {
      const g = groups[key];
      if (!g || g.galleryUrl || key.startsWith("orphan_")) return;
      const candidates = new Set();
      candidates.add(key.includes("_split_") ? key.split("_split_")[0] : key);
      g.rows.forEach((r) => {
        if (r.detail?.galleryId) candidates.add(r.detail.galleryId);
        if (r.detail?.uniqueId) candidates.add(r.detail.uniqueId);
      });
      for (const id of candidates) {
        if (urlByLayoutId.has(id)) {
          g.galleryUrl = urlByLayoutId.get(id);
          break;
        }
      }
    });
  }

  // ── Unified session-gap split (replaces old Pass 5 + Pass 6) ─────────────
  // Split every non-orphan group wherever two consecutive events are separated
  // by more than SESSION_GAP_MS (90 minutes). This handles all three cases:
  //
  //   • Viewer-only groups:  tabs left open overnight, return gallery visits
  //   • Anchored groups:     viewers opening a link hours after upload
  //   • Direct-editor returns: agent comes back via dashboard, no webview_opened
  //     fires, so the old Pass 6 (which only split at webview_opened) could not
  //     catch this. The unified pass catches it because any >90-min gap splits.
  //
  // One exception: the floorplan_uploaded → gallery_ready pair is never split
  // because AI processing can legitimately exceed 90 minutes for large files.
  //
  // The 90-minute threshold is long enough to absorb realistic single-session
  // idle gaps (bathroom break, phone call, brief meeting) without splitting a
  // continuous session, but short enough that genuine "came back later" visits
  // become their own card. Tune here if the heuristic ever drifts.
  //
  // First chunk (oldest, contains upload anchor events when present) keeps the
  // original key. Later chunks get `{key}_split_{chunkStartTimestamp}` keys and
  // are appended to order; the final sort places them chronologically. Each
  // new chunk inherits the parent's `galleryUrl` so the dropdown link works.
  const SESSION_GAP_MS = 90 * 60 * 1000; // 90 minutes

  const splittableKeys = order.filter((k) => {
    if (k.startsWith("orphan_")) return false;
    const g = groups[k];
    return g && g.rows.length >= 2;
  });

  splittableKeys.forEach((key) => {
    const group = groups[key];
    if (!group) return;

    const asc = [...group.rows].sort(
      (a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0)
    );

    const splitPoints = [];
    for (let i = 1; i < asc.length; i++) {
      // Protect the upload pipeline — AI processing can take a long time.
      if (
        asc[i - 1].meta?.event === "floorplan_uploaded" &&
        asc[i].meta?.event === "gallery_ready"
      )
        continue;
      const prev = new Date(asc[i - 1].timestamp || 0).getTime();
      const curr = new Date(asc[i].timestamp || 0).getTime();
      if (curr - prev > SESSION_GAP_MS) splitPoints.push(i);
    }
    if (splitPoints.length === 0) return;

    const chunks = [];
    let start = 0;
    splitPoints.forEach((sp) => {
      chunks.push(asc.slice(start, sp));
      start = sp;
    });
    chunks.push(asc.slice(start));

    // First chunk keeps the original key (preserves anchor events in place).
    group.rows = chunks[0];

    // Subsequent chunks become new session cards; final sort orders them.
    // Inherit the parent's galleryUrl so split-off chunks render a working
    // link instead of falling back to a reconstructed (and often broken) URL.
    chunks.slice(1).forEach((chunk) => {
      const chunkStart = new Date(chunk[0].timestamp || 0).getTime();
      const newKey = `${key}_split_${chunkStart}`;
      groups[newKey] = {
        key: newKey,
        ref: group.ref,
        galleryUrl: group.galleryUrl,
        rows: chunk,
      };
      order.push(newKey);
    });
  });

  // ── Final output: sort rows within each group asc, then sort groups ───────
  // newest-first by their latest event. This guarantees correct ordering
  // regardless of the pass that created each group (Pass 2 orphans and Pass 6
  // splits would otherwise appear out of order).
  const latestTs = (g) =>
    g.rows.length
      ? Math.max(...g.rows.map((r) => new Date(r.timestamp || 0).getTime()))
      : 0;

  return order
    .map((k) => {
      const g = groups[k];
      g.rows.sort((a, b) => new Date(a.timestamp || 0) - new Date(b.timestamp || 0));
      return g;
    })
    .sort((a, b) => latestTs(b) - latestTs(a));
}

// Timeline events rendered with solid dots in the expanded view. These
// represent the milestones an admin scans for: file submission, success,
// signup completion, save, share, download, and inquiry. Other events
// render as hollow dots.
const KEY_TIMELINE_EVENTS = new Set([
  "floorplan_uploaded",
  "upload_failed",
  "gallery_ready",
  "account_created",
  "layout_saved",
  "link_copied",
  "link_shared",
  "download_layout",
  "inquiry_submitted",
]);

// "Atomic action" events that legitimately fire many times in rapid succession
// (the user mashes Shuffle to try arrangements, etc.). Only ADJACENT runs of
// these get collapsed into a single timeline row with an N× badge.
//
// State-transition events (editor_opened, editor_closed, webview_opened) are
// deliberately NOT in this set — each one is a distinct moment in the visit
// and stays as its own row in chronological order. That way a user who edits,
// clicks Done, looks at the gallery, and re-enters the editor reads as
// "editor_opened → editor_closed → editor_opened" instead of being silently
// collapsed into a single "Editor Opened 2×" line that hides the round-trip.
const COLLAPSIBLE_TIMELINE_EVENTS = new Set([
  "shuffle_room",
  "furnish_room",
  "add_items_manually",
]);

// Events that indicate active engagement in the editor (used to bump the
// session's funnel depth from `viewed` to `edited` in classifySession).
const EDITOR_EVENT_KEYS = new Set([
  "shuffle_room",
  "editor_opened",
  "add_items_manually",
  "furnish_room",
  "reset_layout",
  "version_duplicated",
]);

// ── Two-axis session classification ──────────────────────────────────────
//
// Each session card surfaces (a) the entry vector — how the session began —
// and (b) the funnel depth — the deepest stage the user reached. A separate
// optional flag indicates whether the session triggered an in-flow signup
// conversion (auth_wall_triggered + account_created in the same group).
//
// All chips share a neutral background so colour is only carried by text.
// The depth chip text is graded on a single hue scale (gray → indigo →
// blue → teal → green) so an admin scanning the feed can read conversion
// depth at a glance without reading every label.

const ENTRY_CONFIG = {
  upload:  { label: "Upload",        textColor: ANALYTICS_GREEN }, // green — they brought a plan in
  gallery: { label: "Gallery",       textColor: "#0891b2" }, // teal — entered via shared link
  project: { label: "Opened again",  textColor: "#7c3aed" }, // purple — authed user returns to an existing plan
  signup:  { label: "Sign-up",       textColor: "#64748b" }, // slate — orphan account_created
  failed:  { label: "Failed upload", textColor: ANALYTICS_RED }, // red — orphan upload_failed
};

const DEPTH_CONFIG = {
  // "failed" is special: it only applies to upload sessions where the
  // upload itself never produced a gallery, so any further depth talk
  // ("Viewed", "Edited"…) would be nonsensical. The user explicitly
  // flagged that "Upload > Viewed" reads wrong on failures because
  // there is nothing to view.
  failed:     { label: "Failed",     textColor: ANALYTICS_RED },
  viewed:     { label: "Viewed",     textColor: "rgba(0,0,0,0.5)" },
  inquired:   { label: "Interested",   textColor: "#0d9488" },
  edited:     { label: "Edited",     textColor: "#6366f1" },
  saved:      { label: "Saved",      textColor: "#2563eb" },
  shared:     { label: "Shared",     textColor: "#0891b2" },
  downloaded: { label: "Downloaded", textColor: ANALYTICS_GREEN },
};

/**
 * Classify a group into { entry, depth, converted }.
 *   entry      — mutually exclusive priority: failed → signup → upload → gallery → project
 *   depth      — highest funnel stage reached (viewed → inquired → edited → saved → shared → downloaded)
 *   converted  — guest session that triggered the auth wall AND completed signup
 *
 * Failed and signup entries skip depth (no meaningful funnel beyond the entry itself).
 */
function classifySession(group, isOrphan) {
  const evts = new Set(group.rows.map((r) => r.meta?.event));

  const hasUpload       = evts.has("floorplan_uploaded") || evts.has("gallery_ready");
  const hasFailed       = evts.has("upload_failed");
  const hasGalleryReady = evts.has("gallery_ready");
  const hasWebview      = evts.has("webview_opened");
  const hasAccount      = evts.has("account_created");
  const hasAuthWall     = evts.has("auth_wall_triggered");
  const hasEditor       = [...EDITOR_EVENT_KEYS].some((e) => evts.has(e));
  const hasSaved        = evts.has("layout_saved");
  const hasShared       = evts.has("link_copied") || evts.has("link_shared");
  const hasDownload     = evts.has("download_layout");
  const hasInquiry      = evts.has("inquiry_submitted");
  const hasGuestSession = group.rows.some((r) => r.meta?.guestSessionId);

  let entry;
  if (isOrphan && hasFailed && !hasUpload) entry = "failed";
  else if (isOrphan && hasAccount && !hasUpload) entry = "signup";
  else if (hasUpload) entry = "upload";
  else if (hasWebview || (hasGuestSession && (hasEditor || hasWebview))) entry = "gallery";
  else entry = "project"; // signed-in user returns to an existing layout (dashboard or saved link)

  let depth = "viewed";
  if (hasInquiry) depth = "inquired";
  if (hasEditor || hasSaved || hasShared || hasDownload) depth = "edited";
  if (hasSaved) depth = "saved";
  if (hasShared) depth = "shared";
  if (hasDownload) depth = "downloaded";

  // An upload session that fired upload_failed and never produced a
  // gallery_ready is a failure end-state — no view/edit can have
  // happened. Surface "Failed" instead of the default "Viewed" so the
  // row reads honestly (e.g. "Upload › Failed" instead of "Upload ›
  // Viewed"). If gallery_ready landed eventually, the success states
  // take precedence — failures earlier in the same session don't
  // override "Saved/Shared/Downloaded".
  if (hasFailed && !hasGalleryReady && depth === "viewed") {
    depth = "failed";
  }

  // Conversion only meaningful when both signals are present in the same session.
  // Auth wall fires only for guests; account_created confirms they completed.
  const converted = hasAuthWall && hasAccount;

  return { entry, depth, converted };
}

/**
 * Returns "Today", "Yesterday", or a formatted date label.
 *
 * Comparison is done by calendar day in local time so events that fired
 * minutes ago still bucket into "Today".
 */
const bucketLabel = (date, now) => {
  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (sameDay(date, now)) return "Today";

  const y = new Date(now);
  y.setDate(now.getDate() - 1);
  if (sameDay(date, y)) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
  });
};

const formatRelative = (iso) => {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

const formatAbsolute = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const FeedRowSkeleton = () => (
  <Box
    sx={{
      px: 2,
      py: 1.75,
      borderTop: "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Skeleton variant="circular" width={10} height={10} />
    <Box sx={{ flex: 1 }}>
      <Skeleton variant="text" width="40%" />
      <Skeleton variant="text" width="60%" />
    </Box>
    <Skeleton variant="text" width={48} />
  </Box>
);

/**
 * Single ref-bucket card. Collapsed shows thumbnail (left anchor), session
 * type chip, identity, and secondary metadata row. Expanded shows the full
 * event timeline with coloured status dots.
 *
 * Layout (left → right):
 *   [thumbnail] [chip + identity / duration · timestamp · shuffles · id] [⌄]
 *
 * Local state keeps each card's open/closed toggle isolated — sibling cards
 * don't re-render on toggle.
 */
const RefGroupCard = ({
  group,
  onRowClick,
  isFirst = false,
  defaultExpanded = false,
  layoutRefByUuid = null,
  layoutUuidByMongoId = null,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  // Tracks which event-type groups have their sub-rows expanded in the timeline.
  // Resets whenever the card collapses so re-opening always shows the compact view.
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  useEffect(() => {
    if (!expanded) setExpandedEvents(new Set());
  }, [expanded]);

  // Fetch the saved layout to get the most current furnished image (same
  // source as FloorPlanCard on the user dashboard). Only for real layouts —
  // orphan groups have no valid layoutId so we skip the fetch.
  const isOrphanKey = group.key.startsWith("orphan_");

  // Split groups have key `{layoutId}_split_{timestamp}` — extract the real
  // layout ID for gallery URL construction.
  const layoutIdForQuery = group.key.includes("_split_")
    ? group.key.split("_split_")[0]
    : group.key;

  const shuffleCount = group.rows.filter(
    (r) => r.meta?.event === "shuffle_room"
  ).length;
  const firstRow = group.rows[0];
  const lastRow = group.rows[group.rows.length - 1];

  // True when the last event fired within the live threshold.
  const isLive =
    lastRow?.timestamp
      ? Date.now() - new Date(lastRow.timestamp).getTime() < LIVE_THRESHOLD_MS
      : false;

  const uploadEvent = group.rows.find(
    (r) => r.meta?.event === "floorplan_uploaded"
  );
  const galleryReadyEvent = group.rows.find(
    (r) => r.meta?.event === "gallery_ready"
  );
  const processingMs =
    uploadEvent && galleryReadyEvent
      ? new Date(galleryReadyEvent.timestamp) - new Date(uploadEvent.timestamp)
      : null;
  const hasAuthWall = group.rows.some(
    (r) => r.meta?.event === "auth_wall_triggered"
  );

  // ── Session classification (entry × depth × converted) ──────────────────
  const failureEvent = group.rows.find(
    (r) => r.meta?.event === "upload_failed"
  );
  const isOrphan = isOrphanKey;

  const { entry, depth, converted } = classifySession(group, isOrphan);
  const isFailed = entry === "failed";
  const isFailedUpload = Boolean(failureEvent) && !galleryReadyEvent;
  const isSignup = entry === "signup";
  // Failed and signup entries don't surface a depth chip — the entry alone is the story.
  const showDepthChip = !isFailed && !isSignup;

  const entryConfig = ENTRY_CONFIG[entry];
  const depthConfig = DEPTH_CONFIG[depth];

  // ── Gallery URL ──────────────────────────────────────────────────────────
  // Resolution priority:
  //   1. group.galleryUrl — authoritative URL captured from a gallery_ready
  //      event during grouping and propagated to split-off chunks.
  //   2. galleryReadyEvent.detail.galleryUrl — same value, in-row safety net.
  //   3. Locally constructed `/floor-gallery/{layoutIdForQuery}` — used ONLY
  //      when the layoutId is in MongoDB ObjectId format. For UUID-format
  //      layoutIds (common when the original gallery_ready event is outside
  //      the loaded time-range window and the only retained events are
  //      editor-driven), the constructed URL would point at a route whose
  //      lookup is ObjectId-only, opening an infinite spinner. We render
  //      no link in that case — the rest of the dropdown is unaffected.
  const constructedGalleryUrl =
    !isOrphan && isLikelyObjectId(layoutIdForQuery)
      ? `${window.location.origin}/floor-gallery/${layoutIdForQuery}`
      : null;
  const groupGalleryUrl =
    group.galleryUrl ||
    galleryReadyEvent?.detail?.galleryUrl ||
    constructedGalleryUrl;

  // ── Identity ─────────────────────────────────────────────────────────────
  // The depth chip carries the "what they did" signal, so the identity row no
  // longer encodes activity (no more "Anonymous viewer" vs "Anonymous editor").
  // Authenticated users show "by {user}", guests show "Guest". Conversion is
  // surfaced via a separate "✓ Signed up" chip alongside the entry/depth chips.
  const rawIdentity = formatSessionIdentityLabel(firstRow, group.rows);
  // Prepend "by " for resolved users so the row reads "5m ago by Alice (alice@…)".
  // Guests stay plain ("Guest") because "by Guest" reads awkwardly.
  const identityLabel = rawIdentity === 'Guest' ? rawIdentity : `by ${rawIdentity}`;

  const mongoLayoutKey = group.key.includes("_split_")
    ? group.key.split("_split_")[0]
    : group.key;
  const uuidFromCatalog =
    layoutUuidByMongoId instanceof Map ? layoutUuidByMongoId.get(mongoLayoutKey) : null;
  const layoutUuidForLink =
    extractLayoutUuidFromGroup(group) ||
    (uuidFromCatalog && isLayoutUuid(uuidFromCatalog) ? uuidFromCatalog : null) ||
    (isLayoutUuid(mongoLayoutKey) ? mongoLayoutKey : null);
  const linkRefFromApi =
    layoutUuidForLink && layoutRefByUuid instanceof Map
      ? layoutRefByUuid.get(layoutUuidForLink)
      : null;
  const displayListingRef = isRealCustomerRef(group.ref)
    ? group.ref
    : isRealCustomerRef(linkRefFromApi)
      ? linkRefFromApi
      : null;
  const listingLinked = Boolean(displayListingRef);

  // ── Short ID removed from card header (still used in dropdown via groupGalleryUrl)

  // ── Left-column status dot ───────────────────────────────────────────────
  // Pulse only within the live window (<15 min). Unique keyframe names per
  // color avoid emotion deduplicating conflicting @keyframes definitions.
  const shouldPulse = isLive;
  const dotBgColor  = isFailedUpload ? ANALYTICS_RED : ANALYTICS_GREEN;
  const animName    = isFailedUpload ? 'activityDotRed' : 'activityDotGreen';
  const pulseRipple = isFailedUpload ? analyticsRedAlpha(0.4) : analyticsGreenAlpha(0.4);

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderTop: isFirst
          ? { xs: '1px solid rgba(0,0,0,0.06)', md: 'none' }
          : '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      <ButtonBase
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        sx={{
          width: '100%',
          textAlign: 'left',
          justifyContent: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.25,
          minHeight: { xs: 48, sm: 52 },
          // Defeats global `button { border-radius: 50px !important; text-transform: capitalize !important }` in index.css
          borderRadius: '0 !important',
          textTransform: 'none !important',
          transition: 'background 120ms ease',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
          '&:active': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        {/* ── Status dot (left column) ─────────────────────────────── */}
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            flexShrink: 0,
            bgcolor: dotBgColor,
            ...(shouldPulse && {
              [`@keyframes ${animName}`]: {
                '0%, 100%': { boxShadow: `0 0 0 0 ${pulseRipple}` },
                '60%':       { boxShadow: '0 0 0 5px rgba(0,0,0,0)' },
              },
              animation: `${animName} 2s ease-out infinite`,
            }),
          }}
        />

        {/* ── Text column ──────────────────────────────────────────────── */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Row 1: entry · depth · signed-up · Nx variations · live dot — plain text separated by · */}
          <Typography
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: 'rgba(0,0,0,0.85)',
              lineHeight: 1.35,
              mb: 0.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            noWrap
          >
            {[
              entryConfig.label,
              showDepthChip ? depthConfig.label : null,
              converted ? '✓ Signed up' : null,
              shuffleCount > 0 ? `${shuffleCount} ${shuffleCount === 1 ? 'variation' : 'variations'}` : null,
            ]
              .filter(Boolean)
              .flatMap((item, i) =>
                i === 0
                  ? [item]
                  : [
                      <ChevronRightIcon
                        key={`sep-${i}`}
                        sx={{
                          fontSize: '0.55rem',
                          opacity: 0.48,
                          mx: 0.25,
                          verticalAlign: 'middle',
                          flexShrink: 0,
                        }}
                      />,
                      item,
                    ]
              )}
          </Typography>

          {/* Row 2: "{relative time} {identity}" — compact single line */}
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 400,
              color: 'rgba(0,0,0,0.45)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              lineHeight: 1.35,
            }}
            noWrap
          >
            {formatRelative(lastRow?.timestamp)}{' '}
            {identityLabel}
          </Typography>
          {listingLinked && (
            <Typography
              sx={{
                fontSize: '0.72rem',
                color: analyticsGreenAlpha(0.95),
                mt: 0.35,
                lineHeight: 1.3,
              }}
              noWrap
            >
              {formatLinkedListingLabel(displayListingRef)}
            </Typography>
          )}
        </Box>

        {/* ── Expand/collapse chevron ─────────────────────────────────── */}
        <ChevronRightIcon
          sx={{
            color: 'rgba(0,0,0,0.25)',
            fontSize: 16,
            flexShrink: 0,
            transform: expanded ? 'rotate(-90deg)' : 'rotate(90deg)',
            transition: 'transform 160ms ease',
          }}
        />
      </ButtonBase>

      {expanded && (
        <Box
          sx={{
            px: 2,
            pt: 1.25,
            pb: 1.5,
            borderTop: "1px solid rgba(0,0,0,0.06)",
            bgcolor: "rgba(0,0,0,0.015)",
          }}
        >
          {/* Failure reason */}
          {isFailed && (
            <Typography
              sx={{
                display: "block",
                color: ANALYTICS_RED,
                fontSize: "0.8125rem",
                mb: 0.75,
              }}
            >
              {humanizeUploadFailure(
                failureEvent?.detail?.errorType,
                failureEvent?.detail?.errorMessage
              )}
              {' · '}
              <Link
                href={MF_SUPPORT_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                underline="hover"
                sx={{
                  color: "inherit",
                  fontSize: "inherit",
                  fontWeight: 500,
                  textTransform: "none !important",
                }}
              >
                Contact Support
              </Link>
            </Typography>
          )}

          {/* Gallery URL — indigo link with open-in-new icon; visible only in dropdown */}
          {groupGalleryUrl && !isOrphan && (
            <Box sx={{ mb: 0.5 }}>
              <Link
                href={groupGalleryUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                underline="hover"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.4,
                  fontSize: "0.75rem",
                  fontFamily: ANALYTICS_FONT_FAMILY,
                  color: "#6366f1",
                  fontWeight: 500,
                  textTransform: "none !important",
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                }}
              >
                {groupGalleryUrl}
              </Link>
            </Box>
          )}

          {/* Session start timestamp */}
          <Typography
            sx={{
              display: "block",
              color: "rgba(0,0,0,0.3)",
              fontSize: "0.75rem",
              lineHeight: 1.3,
              mb: 1,
            }}
          >
            {"Started "}
            {formatAbsolute(firstRow?.timestamp)}
            {firstRow?.timestamp ? `  ·  ${formatRelative(firstRow.timestamp)}` : ""}
          </Typography>

          {/* Event timeline — chronological, oldest first. State-transition
               events (editor opens, editor closes, gallery views) get their
               own row each time they happen. Only "atomic action" events
               (shuffles, furnishes, item adds) that fired in adjacent
               succession get bundled into one row with an N× badge that
               expands to show individual timestamps.
               Layout: vertical connector line + dot + "Nx EventName  ·  8h ago" */}
          {(() => {
            const groupedTimeline = [];
            [...group.rows].reverse().forEach((row) => {
              const key = row.meta?.event;
              const last = groupedTimeline[groupedTimeline.length - 1];
              if (
                last &&
                last.evtKey === key &&
                COLLAPSIBLE_TIMELINE_EVENTS.has(key)
              ) {
                last.rows.push(row);
              } else {
                groupedTimeline.push({ evtKey: key, rows: [row] });
              }
            });
            return (
              <Box sx={{ position: 'relative' }}>
                {/* Vertical connector line — adjusted for 4px content nudge */}
                <Box
                  aria-hidden="true"
                  sx={{
                    position: 'absolute',
                    left: 7.5,
                    top: 6,
                    bottom: 6,
                    width: '1px',
                    bgcolor: 'rgba(0,0,0,0.1)',
                    pointerEvents: 'none',
                  }}
                />
                <Stack spacing={0.25} sx={{ pl: '4px' }}>
                  {groupedTimeline.map(({ evtKey, rows: evtRows }, timelineIdx) => {
                    const row = evtRows[0]; // most recent occurrence in the cluster
                    const isMultiple = evtRows.length > 1;
                    // Cluster-scoped id so multiple clusters of the same event
                    // type (e.g. two shuffle bursts separated by an editor open)
                    // get independent React keys AND independent expand state.
                    const clusterId = `${evtKey}-${timelineIdx}`;
                    const isGroupExpanded = expandedEvents.has(clusterId);
                    const isKey = KEY_TIMELINE_EVENTS.has(evtKey);
                    const dotSx =
                      evtKey === "gallery_ready"
                        ? { bgcolor: ANALYTICS_GREEN }
                        : evtKey === "upload_failed"
                        ? { bgcolor: ANALYTICS_RED }
                        : isKey
                        ? { bgcolor: "rgba(0,0,0,0.75)" }
                        : {
                            bgcolor: "#fff",
                            border: "1.5px solid rgba(0,0,0,0.2)",
                          };
                    let suffix = "";
                    if (evtKey === "gallery_ready" && processingMs != null) {
                      suffix = ` · ${Math.round(processingMs / 1000)}s`;
                    } else if (evtKey === "layout_saved" && hasAuthWall) {
                      suffix = " · post-signup";
                    }
                    return (
                      <Box key={clusterId} sx={{ pl: 0 }}>
                        {/* Primary row */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                            gap: 0.5,
                          }}
                        >
                          {/* Timeline dot — left column, outside buttons */}
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              flexShrink: 0,
                              zIndex: 1,
                              ...dotSx,
                            }}
                          />

                          {/* Expand toggle — leading icon, no fill */}
                          {isMultiple && (
                            <ButtonBase
                              onClick={() =>
                                setExpandedEvents((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(clusterId)) next.delete(clusterId);
                                  else next.add(clusterId);
                                  return next;
                                })
                              }
                              sx={{
                                flexShrink: 0,
                                p: 0.4,
                                minWidth: 20,
                                minHeight: 24,
                                borderRadius: "4px !important",
                                textTransform: "none !important",
                                transition: "background 120ms ease",
                                "&:hover": { bgcolor: "rgba(0,0,0,0.06)" },
                              }}
                            >
                              <ChevronRightIcon
                                sx={{
                                  fontSize: 12,
                                  color: "rgba(0,0,0,0.35)",
                                  transform: isGroupExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                  transition: "transform 160ms ease",
                                }}
                              />
                            </ButtonBase>
                          )}

                          {/* Main content: count + event name + timestamp */}
                          <ButtonBase
                            onClick={() =>
                              onRowClick?.(row, { galleryUrl: groupGalleryUrl })
                            }
                            sx={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              gap: 1,
                              textAlign: "left",
                              borderRadius: "6px !important",
                              pl: 0.75,
                              pr: 1,
                              py: 0.4,
                              minWidth: 0,
                              textTransform: "none !important",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                            }}
                          >
                            {/* Count badge (always shown when >1) */}
                            {isMultiple && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 400,
                                  color: "rgba(0,0,0,0.35)",
                                  lineHeight: 1,
                                  flexShrink: 0,
                                  minWidth: 20,
                                }}
                              >
                                {evtRows.length}×
                              </Typography>
                            )}

                            {/* Event name */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.875rem",
                                color: isKey ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0.5)",
                                lineHeight: 1.4,
                                fontWeight: isKey ? 500 : 400,
                                flex: 1,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {getEventInfo(evtKey).alias}
                              {suffix && (
                                <Box
                                  component="span"
                                  sx={{ color: "rgba(0,0,0,0.4)", fontWeight: 400 }}
                                >
                                  {suffix}
                                </Box>
                              )}
                            </Typography>

                            {/* Timestamp — nudged 2px left */}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "0.75rem",
                                color: "rgba(0,0,0,0.35)",
                                lineHeight: 1.4,
                                flexShrink: 0,
                                mr: "-2px",
                              }}
                            >
                              {formatRelative(row.timestamp)}
                            </Typography>
                          </ButtonBase>
                        </Box>

                        {/* Sub-rows — individual timestamps when expanded */}
                        {isMultiple && isGroupExpanded && (
                          <Stack spacing={0} sx={{ pl: 2.5, mt: 0.15 }}>
                            {evtRows.map((subRow, i) => (
                              <ButtonBase
                                key={subRow._id || `${clusterId}-sub-${i}`}
                                onClick={() =>
                                  onRowClick?.(subRow, { galleryUrl: groupGalleryUrl })
                                }
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-start',
                                  gap: 1,
                                  width: '100%',
                                  textAlign: 'left',
                                  borderRadius: '4px !important',
                                  px: 1,
                                  py: 0.3,
                                  textTransform: 'none !important',
                                  '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(0,0,0,0.16)',
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.8125rem',
                                    color: 'rgba(0,0,0,0.32)',
                                    lineHeight: 1.5,
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  {getEventInfo(evtKey).alias}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: '0.75rem',
                                    color: 'rgba(0,0,0,0.26)',
                                    lineHeight: 1.4,
                                    flexShrink: 0,
                                  }}
                                >
                                  {formatRelative(subRow.timestamp)}
                                </Typography>
                              </ButtonBase>
                            ))}
                          </Stack>
                        )}
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            );
          })()}
        </Box>
      )}
    </Box>
  );
};

const FeedList = ({
  rows,
  loading,
  hasMore,
  loadingMore,
  onLoadMore,
  onRowClick,
  groupByRef = false,
  initialSessions = SESSIONS_PER_PAGE,
  sessionsPerPage = SESSIONS_PER_PAGE,
  embedded = false,
  defaultExpandedFirst = false,
  layoutRefByUuid = null,
  layoutUuidByMongoId = null,
}) => {
  const sorted = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    return [...rows].sort((a, b) => {
      const at = new Date(a.timestamp || 0).getTime();
      const bt = new Date(b.timestamp || 0).getTime();
      return bt - at;
    });
  }, [rows]);

  const groups = useMemo(() => {
    const now = new Date();
    const buckets = new Map();
    sorted.forEach((row) => {
      const ts = row.timestamp ? new Date(row.timestamp) : new Date(0);
      const label = bucketLabel(ts, now);
      if (!buckets.has(label)) buckets.set(label, []);
      buckets.get(label).push(row);
    });
    return Array.from(buckets.entries());
  }, [sorted]);

  // Pre-compute grouped upload cards so re-renders from parent state changes
  // don't re-run the O(n²) grouping algorithm unnecessarily.
  const refGroups = useMemo(() => groupEventsByUpload(sorted), [sorted]);

  // Client-side session pagination. Starts at initialSessions; grows as the
  // user clicks "Load more". The parent resets this via a key prop change
  // whenever the date range or partner filter changes.
  const [visibleSessions, setVisibleSessions] = useState(initialSessions);

  // When the parent raises initialSessions (e.g. md+ layout), reveal at least that
  // many without resetting a larger count from prior "Load more" clicks.
  useEffect(() => {
    setVisibleSessions((v) => Math.max(v, initialSessions));
  }, [initialSessions]);

  // Whether more grouped sessions are available locally (no API needed).
  const hasMoreLocal = visibleSessions < refGroups.length;
  // Show the Load More button if either local or remote data is available.
  const showLoadMore = hasMoreLocal || hasMore;

  const handleLoadMoreSessions = () => {
    // Always reveal the next page of already-grouped sessions.
    setVisibleSessions((v) => v + sessionsPerPage);
    // Only hit the API when the local batch is exhausted.
    if (!hasMoreLocal && hasMore) onLoadMore();
  };

  if (loading) {
    return (
      <Box
        sx={embedded ? {} : {
          borderRadius: "14px",
          border: "1px solid rgba(0,0,0,0.06)",
          bgcolor: "#fff",
          overflow: "hidden",
        }}
      >
        {[0, 1, 2, 3, 4].map((i) => (
          <FeedRowSkeleton key={i} />
        ))}
      </Box>
    );
  }

  if (!sorted.length) {
    return (
      <Box
        sx={embedded ? { py: 3, textAlign: "center" } : {
          borderRadius: "14px",
          border: "1px solid rgba(0,0,0,0.06)",
          bgcolor: "#fff",
          py: 6,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No activity in this period.
        </Typography>
      </Box>
    );
  }

  if (groupByRef) {
    const visibleGroups = refGroups.slice(0, visibleSessions);

    const rows_ = visibleGroups.map((g, idx) => (
      <RefGroupCard
        key={g.key}
        group={g}
        onRowClick={onRowClick}
        isFirst={idx === 0}
        defaultExpanded={defaultExpandedFirst && idx === 0}
        layoutRefByUuid={layoutRefByUuid}
        layoutUuidByMongoId={layoutUuidByMongoId}
      />
    ));

    const loadMoreBtn = showLoadMore && (
      <ButtonBase
        onClick={handleLoadMoreSessions}
        disabled={loadingMore}
        sx={{
          width: '100%',
          py: 1.25,
          borderTop: '1px solid rgba(0,0,0,0.06)',
          fontSize: '0.8rem',
          fontWeight: 500,
          color: 'rgba(99,102,241,0.9)',
          // bottom pill matches container radius; top stays square to border
          borderRadius: embedded ? '0 0 14px 14px !important' : '0 0 14px 14px !important',
          textTransform: 'none !important',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' },
          '&.Mui-disabled': { opacity: 0.5 },
        }}
      >
        {loadingMore ? 'Loading…' : 'Load more'}
      </ButtonBase>
    );

    if (embedded) {
      return (
        <Box>
          {rows_}
          {loadMoreBtn}
        </Box>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            borderRadius: '14px',
            border: '1px solid rgba(0,0,0,0.06)',
            bgcolor: '#fff',
            overflow: 'hidden',
          }}
        >
          {rows_}
          {loadMoreBtn}
        </Box>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {groups.map(([label, items]) => (
        <Box key={label}>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 1,
              px: 0.5,
              color: "rgba(0,0,0,0.5)",
              fontWeight: 600,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontSize: "0.7rem",
            }}
          >
            {label}
          </Typography>

          <Box
            sx={{
              borderRadius: "14px",
              border: "1px solid rgba(0,0,0,0.06)",
              bgcolor: "#fff",
              overflow: "hidden",
            }}
          >
            {items.map((row, idx) => {
              const eventKey = row.meta?.event;
              const info = getEventInfo(eventKey);
              const dotColor = getFamilyColor(eventKey);
              const customer = row.meta?.customer_ref;
              const customerLabel =
                customer && !String(customer).includes("not-provided") ? customer : null;

              return (
                <ButtonBase
                  key={row._id || `${eventKey}-${row.timestamp}-${idx}`}
                  onClick={() => onRowClick && onRowClick(row)}
                  sx={{
                    width: "100%",
                    textAlign: "left",
                    justifyContent: "flex-start",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                    // Defeats global `button { border-radius: 50px !important; text-transform: capitalize !important }` in index.css
                    borderRadius: "0 !important",
                    textTransform: "none !important",
                    transition: "background 120ms ease",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                    "&:active": { bgcolor: "rgba(0,0,0,0.04)" },
                    minHeight: 56,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: dotColor,
                      flexShrink: 0,
                    }}
                  />

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                color: "rgba(0,0,0,0.88)",
                lineHeight: 1.25,
                      }}
                    >
                      {info.alias}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(0,0,0,0.5)", fontSize: "0.75rem" }}
                      >
                        {formatRelative(row.timestamp)}
                      </Typography>
                      {customerLabel ? (
                        <>
                          <Box
                            sx={{
                              width: 3,
                              height: 3,
                              borderRadius: "50%",
                              bgcolor: "rgba(0,0,0,0.25)",
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: ANALYTICS_FONT_FAMILY,
                              color: "rgba(0,0,0,0.5)",
                              fontSize: "0.72rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: { xs: 120, sm: 220 },
                            }}
                          >
                            {customerLabel}
                          </Typography>
                        </>
                      ) : null}
                    </Stack>
                  </Box>

                  <ChevronRightIcon
                    sx={{ color: "rgba(0,0,0,0.3)", fontSize: 20, flexShrink: 0 }}
                  />
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      ))}

      {hasMore && (
        <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
          <Button
            variant="text"
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={14} thickness={5} /> : null}
            sx={{
                textTransform: "none",
                color: "rgba(0,0,0,0.5)",
                fontWeight: 600,
                "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
              }}
            >
              {loadingMore ? "Loading…" : "Load more"}
            </Button>
          </Box>
        )}
      </Stack>
    );
  };

export default FeedList;
